import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Restaurant as RestaurantIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const NotFound = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--primary-light) 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
      }}
    >
      {/* Decorative Background Elements */}
      {[
        { size: 400, top: "-15%", right: "-10%", opacity: 0.05 },
        { size: 500, bottom: "-20%", left: "-15%", opacity: 0.05 },
        { size: 250, top: "50%", left: "50%", opacity: 0.04 },
      ].map((circle, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: circle.size,
            height: circle.size,
            borderRadius: "50%",
            bgcolor: "white",
            opacity: circle.opacity,
            top: circle.top,
            left: circle.left,
            bottom: circle.bottom,
            right: circle.right,
            transform: circle.left ? "translate(-50%, -50%)" : "none",
          }}
        />
      ))}

      {/* Floating Food Icons */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "8%",
          fontSize: "3rem",
          opacity: 0.1,
          transform: "rotate(-15deg)",
          animation: "float 6s ease-in-out infinite",
        }}
      >
        🍕
      </Box>
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          right: "10%",
          fontSize: "4rem",
          opacity: 0.1,
          transform: "rotate(10deg)",
          animation: "float 8s ease-in-out infinite",
          animationDelay: "1s",
        }}
      >
        🍔
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: "30%",
          right: "15%",
          fontSize: "2.5rem",
          opacity: 0.08,
          transform: "rotate(25deg)",
          animation: "float 7s ease-in-out infinite",
          animationDelay: "2s",
        }}
      >
        🍜
      </Box>
      <Box
        sx={{
          position: "absolute",
          bottom: "30%",
          left: "12%",
          fontSize: "3rem",
          opacity: 0.08,
          transform: "rotate(-20deg)",
          animation: "float 9s ease-in-out infinite",
          animationDelay: "0.5s",
        }}
      >
        🥘
      </Box>

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            {/* 404 Number with Animation */}
            <Box sx={{ position: "relative", mb: 2 }}>
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 100 }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "6rem", sm: "8rem", md: "10rem" },
                    fontWeight: 900,
                    background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--primary-light) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1,
                    textShadow: "0 4px 20px rgba(255,107,0,0.2)",
                    letterSpacing: "10px",
                  }}
                >
                  404
                </Typography>
              </motion.div>

              {/* Decorative Line */}
              <Box
                sx={{
                  width: 80,
                  height: 4,
                  bgcolor: "var(--primary)",
                  borderRadius: "10px",
                  mx: "auto",
                  mb: 2,
                }}
              />
            </Box>

            {/* Icon */}
            <motion.div
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.5, type: "spring" }}
            >
              <Box
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 80, sm: 100 },
                  borderRadius: "50%",
                  bgcolor: "var(--bg-primary-soft)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <SearchIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: "var(--primary)" }} />
              </Box>
            </motion.div>

            {/* Title */}
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                color: "var(--text)",
                mb: 1,
              }}
            >
              Oops! Page Not Found
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                color: "var(--text-light)",
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                maxWidth: "500px",
                mx: "auto",
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              The page you are looking for might have been removed, 
              had its name changed, or is temporarily unavailable.
            </Typography>

            {/* Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              sx={{ mb: 4 }}
            >
              <Button
                component={Link}
                to="/"
                variant="contained"
                startIcon={<HomeIcon />}
                sx={{
                  bgcolor: "var(--primary)",
                  color: "#fff",
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.2, sm: 1.5 },
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  boxShadow: "var(--shadow-orange)",
                  "&:hover": {
                    bgcolor: "var(--primary-dark)",
                    transform: "translateY(-3px)",
                    boxShadow: "var(--shadow-orange-hover)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Go to Homepage
              </Button>

              <Button
                component={Link}
                to="/"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: "var(--text)",
                  borderColor: "var(--border)",
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.2, sm: 1.5 },
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  "&:hover": {
                    borderColor: "var(--primary)",
                    color: "var(--primary)",
                    bgcolor: "var(--bg-primary-soft)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Go Back
              </Button>
            </Stack>

            {/* Divider */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 3,
              }}
            >
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
              <Typography variant="caption" sx={{ color: "var(--text-light)" }}>
                OR
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
            </Box>

            {/* Quick Links */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ gap: { xs: 1, sm: 2 } }}
            >
              <Button
                component={Link}
                to="/features"
                sx={{
                  color: "var(--text-light)",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    color: "var(--primary)",
                  },
                }}
              >
                Features
              </Button>
              <Button
                component={Link}
                to="/pricing"
                sx={{
                  color: "var(--text-light)",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    color: "var(--primary)",
                  },
                }}
              >
                Pricing
              </Button>
              <Button
                component={Link}
                to="/about"
                sx={{
                  color: "var(--text-light)",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    color: "var(--primary)",
                  },
                }}
              >
                About Us
              </Button>
              <Button
                component={Link}
                to="/contact"
                sx={{
                  color: "var(--text-light)",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    color: "var(--primary)",
                  },
                }}
              >
                Contact
              </Button>
            </Stack>

            {/* Footer Text */}
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "var(--text-light)",
                mt: 4,
                fontSize: "0.75rem",
              }}
            >
              © 2026 RestaurantWala • Your Digital Restaurant Partner
            </Typography>
          </Paper>
        </motion.div>
      </Container>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(5deg);
            }
            100% {
              transform: translateY(0px) rotate(0deg);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default NotFound;